import { useEffect, useMemo, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import TaskSkeleton from "./TaskSkeleton";
import * as XLSX from "xlsx-js-style";

const MAX_SUB = 5;
const MAX_CHILD = 5;

const ROW_GROUP_COLORS = [
  "#BFDBFE",
  "#BBF7D0",
  "#FED7AA",
  "#DDD6FE",
  "#FBCFE8",
  "#A5F3FC",
];

const buildGroupColorMap = (groupKeys) => {
  const map = {};
  let i = 0;
  groupKeys.forEach((k) => {
    const key = String(k ?? "");
    if (!(key in map)) {
      map[key] = ROW_GROUP_COLORS[i % ROW_GROUP_COLORS.length];
      i += 1;
    }
  });
  return map;
};

const money = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const d = (v) => (v ? moment(v).format("DD/MM/YYYY") : "—");

const EXCEL_BORDER = {
  top: { style: "thin", color: { rgb: "D1D5DB" } },
  bottom: { style: "thin", color: { rgb: "D1D5DB" } },
  left: { style: "thin", color: { rgb: "D1D5DB" } },
  right: { style: "thin", color: { rgb: "D1D5DB" } },
};

export default function MsKeyTableView() {
  const { http } = AuthUser();
  const [tree, setTree] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterMaster, setFilterMaster] = useState("");

  useEffect(() => {
    setIsLoading(true);
    http
      .get("/ms_keys_tree")
      .then((res) => {
        setIsLoading(false);
        setTree(res.data.data || []);
      })
      .catch(() => {
        setIsLoading(false);
        toast.error("Error loading table view");
      });
  }, []);

  const flatRows = useMemo(() => {
    const rows = [];
    tree.forEach((master) => {
      const subs = master.sub_keys || [];
      const subUsed = subs.length;
      const subLeft = Math.max(0, MAX_SUB - subUsed);
      const childTotal = subs.reduce((n, s) => n + (s.child_keys || []).length, 0);

      if (subs.length === 0) {
        rows.push({
          id: `m-${master.id}`,
          master_id: master.id,
          master_key_id: master.master_key_id,
          master_duration: master.duration_years,
          master_start: master.activated_date,
          master_end: master.activated_end_date,
          sub_used: subUsed,
          sub_left: subLeft,
          child_total: childTotal,
          share_name: "",
          company_name_sub: "",
          user_name: "",
          user_email: "",
          user_mobile: "",
          sub_installed: "",
          sub_ending: "",
          sub_renew: "",
          child_used: 0,
          child_left: MAX_CHILD,
          child_company: "",
          child_location: "",
          child_contact: "",
          child_phone: "",
          child_installed: "",
          child_duration: "",
          child_ending: "",
          child_renew: "",
          qty: "",
          systems: "",
          rate: "",
          rate_details: "",
          status: "",
          license: "",
        });
        return;
      }

      subs.forEach((sub) => {
        const children = sub.child_keys || [];
        const childUsed = children.length;
        const childLeft = Math.max(0, MAX_CHILD - childUsed);
        const base = {
          master_id: master.id,
          master_key_id: master.master_key_id,
          master_duration: master.duration_years,
          master_start: master.activated_date,
          master_end: master.activated_end_date,
          sub_used: subUsed,
          sub_left: subLeft,
          child_total: childTotal,
          share_name: sub.share_name || "",
          company_name_sub: sub.company_name || "",
          user_name: sub.user_name || "",
          user_email: sub.user_email || "",
          user_mobile: sub.user_mobile || "",
          sub_installed: sub.installed_date,
          sub_ending: sub.ending_on,
          sub_renew: sub.to_renew,
          child_used: childUsed,
          child_left: childLeft,
        };

        if (children.length === 0) {
          rows.push({
            id: `s-${sub.id}`,
            ...base,
            child_company: "",
            child_location: "",
            child_contact: "",
            child_phone: "",
            child_installed: "",
            child_duration: "",
            child_ending: "",
            child_renew: "",
            qty: "",
            systems: "",
            rate: "",
            rate_details: "",
            status: "",
            license: "",
          });
          return;
        }

        children.forEach((c) => {
          rows.push({
            id: `c-${c.id}`,
            ...base,
            child_company: c.company_name || "",
            child_location: c.location || "",
            child_contact: c.contact_person || "",
            child_phone: c.contact_number || "",
            child_installed: c.installed_date,
            child_duration: c.duration_years
              ? `${c.duration_years} Yr`
              : c.duration_days
              ? `${c.duration_days} days`
              : "",
            child_ending: c.ending_on,
            child_renew: c.to_renew,
            qty: c.qty,
            systems: c.total_systems,
            rate: c.rate,
            rate_details: c.rate_details,
            status: c.status || "",
            license: c.license_details || "",
          });
        });
      });
    });
    return rows;
  }, [tree]);

  const filtered = useMemo(() => {
    let rows = flatRows;
    if (filterMaster) {
      rows = rows.filter((r) => String(r.master_id) === String(filterMaster));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        [
          r.master_key_id,
          r.share_name,
          r.company_name_sub,
          r.user_name,
          r.user_email,
          r.child_company,
          r.child_location,
          r.child_contact,
          r.child_phone,
          r.license,
          r.status,
        ]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    return rows;
  }, [flatRows, search, filterMaster]);

  const groupColors = useMemo(
    () => buildGroupColorMap(filtered.map((r) => r.master_id)),
    [filtered]
  );

  const handleExcel = () => {
    if (!filtered.length) {
      toast.error("No rows to export");
      return;
    }
    const headers = [
      "Master Key",
      "Sub Used/Left",
      "Share Name",
      "Sub Company",
      "User",
      "Email",
      "Mobile",
      "Child Used/Left",
      "Child Company",
      "Location",
      "Contact",
      "Phone",
      "Installed",
      "Duration",
      "Ending On",
      "To Renew",
      "Qty",
      "Systems",
      "Rate",
      "Rate Details",
      "Status",
      "License",
    ];
    const data = filtered.map((r) => [
      r.master_key_id || "",
      `${r.sub_used}/${MAX_SUB} (left ${r.sub_left})`,
      r.share_name || "",
      r.company_name_sub || "",
      r.user_name || "",
      r.user_email || "",
      r.user_mobile || "",
      `${r.child_used}/${MAX_CHILD} (left ${r.child_left})`,
      r.child_company || "",
      r.child_location || "",
      r.child_contact || "",
      r.child_phone || "",
      d(r.child_installed),
      r.child_duration || "",
      d(r.child_ending),
      d(r.child_renew),
      r.qty ?? "",
      r.systems ?? "",
      r.rate ?? "",
      r.rate_details ?? "",
      r.status || "",
      r.license || "",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = range.s.r; R <= range.e.r; R += 1) {
      for (let C = range.s.c; C <= range.e.c; C += 1) {
        const ref = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[ref]) continue;
        ws[ref].s =
          R === 0
            ? {
                fill: { fgColor: { rgb: "0F766E" } },
                font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10 },
                alignment: { horizontal: "center", vertical: "center", wrapText: true },
                border: EXCEL_BORDER,
              }
            : {
                fill: { fgColor: { rgb: R % 2 ? "F8FAFC" : "FFFFFF" } },
                font: { sz: 9 },
                alignment: { vertical: "center" },
                border: EXCEL_BORDER,
              };
      }
    }
    ws["!cols"] = headers.map(() => ({ wch: 14 }));
    ws["!cols"][0] = { wch: 28 };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MS Table");
    XLSX.writeFile(wb, `ms_key_table_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
  };

  return (
    <div className="ms-table-page">
      <Toaster />
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h4 className="mb-1">MS Key — Table View</h4>
          <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
            Flat table: Master · Sub · Child (max {MAX_SUB} sub / {MAX_CHILD} child)
          </p>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-success"
          disabled={!filtered.length}
          onClick={handleExcel}
        >
          ⬇ Excel
        </button>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: "280px" }}
          value={filterMaster}
          onChange={(e) => setFilterMaster(e.target.value)}
        >
          <option value="">All Master Keys</option>
          {tree.map((m) => (
            <option key={m.id} value={m.id}>
              {m.master_key_id}
            </option>
          ))}
        </select>
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: "320px" }}
          placeholder="Search master / sub / company / contact..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <TaskSkeleton />
      ) : (
        <div className="table-responsive ms-table-wrap">
          <table className="table table-bordered table-hover align-middle mb-0 ms-flat-table">
            <thead>
              <tr>
                <th colSpan="3" className="grp-master">
                  Master
                </th>
                <th colSpan="5" className="grp-sub">
                  Sub Key
                </th>
                <th colSpan="12" className="grp-child">
                  Child Key
                </th>
              </tr>
              <tr>
                <th>Master Key</th>
                <th>Sub Used</th>
                <th>Sub Left</th>
                <th>Share Name</th>
                <th>Company</th>
                <th>User</th>
                <th>Child Used</th>
                <th>Child Left</th>
                <th>Company</th>
                <th>Location</th>
                <th>Contact</th>
                <th>Phone</th>
                <th>Installed</th>
                <th>Duration</th>
                <th>Ending</th>
                <th>Renew</th>
                <th>Qty</th>
                <th>Systems</th>
                <th>Rate</th>
                <th>Rate Details</th>
                <th>Status</th>
                <th>License</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="22" className="text-center text-muted py-4">
                    No records found
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr
                    key={r.id}
                    className="group-row"
                    style={{
                      "--row-bg": groupColors[String(r.master_id)] || "#BFDBFE",
                    }}
                  >
                    <td className="fw-semibold master-cell">{r.master_key_id || "—"}</td>
                    <td className="text-center">
                      <span className="badge bg-primary-subtle text-primary border">
                        {r.sub_used}/{MAX_SUB}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`badge ${r.sub_left === 0 ? "bg-danger" : "bg-success"}`}>
                        {r.sub_left}
                      </span>
                    </td>
                    <td>{r.share_name || "—"}</td>
                    <td>{r.company_name_sub || "—"}</td>
                    <td>
                      <div>{r.user_name || "—"}</div>
                      <small className="text-muted">{r.user_email || ""}</small>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-info-subtle text-dark border">
                        {r.child_used}/{MAX_CHILD}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`badge ${r.child_left === 0 ? "bg-danger" : "bg-success"}`}>
                        {r.child_left}
                      </span>
                    </td>
                    <td className="fw-semibold">{r.child_company || "—"}</td>
                    <td>{r.child_location || "—"}</td>
                    <td>{r.child_contact || "—"}</td>
                    <td>{r.child_phone || "—"}</td>
                    <td className="text-nowrap">{d(r.child_installed)}</td>
                    <td>{r.child_duration || "—"}</td>
                    <td className="text-nowrap">{d(r.child_ending)}</td>
                    <td className="text-nowrap">{d(r.child_renew)}</td>
                    <td>{r.qty ?? "—"}</td>
                    <td>{r.systems ?? "—"}</td>
                    <td>{money(r.rate)}</td>
                    <td className="fw-semibold">{money(r.rate_details)}</td>
                    <td>
                      {r.status ? (
                        <span
                          className={`badge ${
                            r.status === "Active"
                              ? "bg-success"
                              : r.status === "Expired"
                              ? "bg-danger"
                              : "bg-secondary"
                          }`}
                        >
                          {r.status}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={{ maxWidth: "160px" }} title={r.license || ""}>
                      {r.license || "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .ms-table-page { padding: 16px 20px 28px; }
        .ms-table-wrap {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          overflow: auto;
          max-height: 75vh;
          background: #fff;
        }
        .ms-flat-table { font-size: 12px; margin: 0; }
        .ms-flat-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          white-space: nowrap;
          font-size: 11px;
          vertical-align: middle;
        }
        .ms-flat-table thead tr:first-child th {
          top: 0;
          text-align: center;
          font-weight: 800;
          letter-spacing: .04em;
        }
        .ms-flat-table thead tr:nth-child(2) th {
          top: 34px;
          background: #f1f5f9 !important;
          color: #334155;
        }
        .grp-master { background: #0f172a !important; color: #fff !important; }
        .grp-sub { background: #0f766e !important; color: #fff !important; }
        .grp-child { background: #1d4ed8 !important; color: #fff !important; }
        .ms-flat-table td { white-space: nowrap; }
        .ms-flat-table tbody tr.group-row > td {
          background-color: var(--row-bg) !important;
        }
        .ms-flat-table tbody tr.group-row:hover > td { filter: brightness(0.97); }
      `}</style>
    </div>
  );
}
